import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { gql, useQuery } from '@apollo/client';
import React from 'react';
import { Alert, Button, Checkbox, InputNumber, Select, Space, Table, Typography } from 'antd';

export type CustomFieldsImportMappingRow = {
  id: string;
  columnIndex?: number;
  customFieldCode?: string;
  addNewOption: boolean;
  replace: boolean;
  maxValue: boolean;
};

type CustomFieldOption = {
  code: string;
  heading: string;
  fieldTypeEnum: string;
  allowMultiple?: boolean | null;
  allowUserCreate?: boolean | null;
};

type CustomFieldsImportMappingProps = {
  rows: CustomFieldsImportMappingRow[];
  setRows: React.Dispatch<React.SetStateAction<CustomFieldsImportMappingRow[]>>;
  fixedColumns: FixedColumn[];
  firstCustomFieldColumn: number;
};

type FixedColumn = {
  id: string | number;
  lineDataIndex: number;
  field: {
    code: string;
    heading?: string | null;
    note?: string | null;
  };
};

const fixedColumnHeading = (row: FixedColumn) => (
  row.field.code === 'PAPBID' ? 'aPB ID' : row.field.heading ?? row.field.code
);

const CustomFieldsImportMapping: React.FC<CustomFieldsImportMappingProps> = ({
  rows,
  setRows,
  fixedColumns,
  firstCustomFieldColumn,
}) => {
  const { data, loading } = useQuery<{
    customFields: {
      nodes: CustomFieldOption[];
    };
  }>(CUSTOM_FIELDS_QUERY);

  const customFields = data?.customFields.nodes ?? [];
  const customFieldsByCode = new Map(customFields.map(field => [field.code, field]));
  const isSingleValue = (field?: CustomFieldOption) => field?.allowMultiple !== true;
  const isMultiValue = (field?: CustomFieldOption) => field?.allowMultiple === true;
  const isSingleValueNumber = (field?: CustomFieldOption) => (
    !!field && isSingleValue(field) && field.fieldTypeEnum === 'NUMBER'
  );
  const isSingleValueText = (field?: CustomFieldOption) => (
    !!field && isSingleValue(field) && field.fieldTypeEnum === 'TEXT'
  );
  const isOptionField = (field?: CustomFieldOption) => (
    !!field && ['CHECK', 'SELECT', 'TAG'].includes(field.fieldTypeEnum)
  );
  const canAddNewOption = (field?: CustomFieldOption) => (
    isOptionField(field) && field?.allowUserCreate === true
  );

  const updateRow = (
    id: string,
    patch: Partial<CustomFieldsImportMappingRow>
  ) => {
    setRows(current => current.map(row => {
      if (row.id !== id) return row;
      const next = { ...row, ...patch };
      const selectedField = next.customFieldCode
        ? customFieldsByCode.get(next.customFieldCode)
        : undefined;
      if (patch.customFieldCode !== undefined) {
        next.replace = isSingleValue(selectedField) && !isSingleValueText(selectedField);
        next.maxValue = false;
      }
      if (!canAddNewOption(selectedField)) next.addNewOption = false;
      if (!isSingleValueNumber(selectedField)) {
        next.maxValue = false;
      }
      if (isSingleValue(selectedField) && !isSingleValueText(selectedField)) {
        next.replace = true;
      }
      return next;
    }));
  };

  const addRow = () => {
    const nextColumnIndex = rows
      .map(row => row.columnIndex)
      .filter((value): value is number => value !== undefined)
      .reduce((max, value) => Math.max(max, value), firstCustomFieldColumn - 1) + 1;

    setRows(current => [
      ...current,
      {
        id: `${Date.now()}-${current.length}`,
        columnIndex: nextColumnIndex,
        addNewOption: false,
        replace: false,
        maxValue: false,
      },
    ]);
  };

  const removeRow = (id: string) => {
    setRows(current => current.filter(row => row.id !== id));
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={4}>Custom fields import mapping</Typography.Title>
      <Alert
        type="info"
        showIcon
        message="Fixed columns"
        description={[
          'These columns are read by the custom import processor. ',
          'Add rows below for extra CSV columns that should be imported as person custom fields.',
        ].join('')}
      />
      <Table
        size="small"
        pagination={false}
        rowKey="id"
        dataSource={fixedColumns}
        columns={[
          {
            title: 'Column index',
            dataIndex: 'lineDataIndex',
            width: 140,
          },
          {
            title: 'Column name',
            render: (_, row: FixedColumn) => fixedColumnHeading(row),
            width: 220,
          },
          {
            title: 'Description',
            dataIndex: ['field', 'note'],
          },
        ]}
      />
      <Space direction="vertical" size={0}>
        <Typography.Text type="secondary">
          <b>New option:</b>
          {' '}
          checked creates new options; unchecked uses existing options only.
        </Typography.Text>
        <Typography.Text type="secondary">
          <b>Replace:</b>
          {' '}
          checked replaces existing person values; unchecked adds to them. Single-value Text fields can append to existing text.
        </Typography.Text>
        <Typography.Text type="secondary">
          <b>Max value:</b>
          {' '}
          for single-value Number fields, saves only higher values.
        </Typography.Text>
      </Space>
      <Table<CustomFieldsImportMappingRow>
        size="small"
        pagination={false}
        rowKey="id"
        dataSource={rows}
        locale={{ emptyText: 'No custom field columns selected' }}
        columns={[
          {
            title: 'CSV column',
            dataIndex: 'columnIndex',
            width: 130,
            render: (_, row) => (
              <InputNumber
                min={firstCustomFieldColumn}
                precision={0}
                value={row.columnIndex}
                onChange={value => updateRow(row.id, { columnIndex: value ?? undefined })}
                style={{ width: '100%' }}
              />
            ),
          },
          {
            title: 'Custom field',
            dataIndex: 'customFieldCode',
            render: (_, row) => (
              <Select
                showSearch
                loading={loading}
                value={row.customFieldCode}
                placeholder="Select custom field"
                optionFilterProp="label"
                onChange={customFieldCode => updateRow(row.id, { customFieldCode })}
                style={{ width: '100%' }}
                options={customFields.map(field => ({
                  value: field.code,
                  label: `${field.heading} (${field.code})`,
                }))}
              />
            ),
          },
          {
            title: 'New option',
            dataIndex: 'addNewOption',
            width: 120,
            render: (_, row) => {
              const selectedField = row.customFieldCode
                ? customFieldsByCode.get(row.customFieldCode)
                : undefined;
              const disabled = !canAddNewOption(selectedField);

              return (
                <Checkbox
                  checked={row.addNewOption && !disabled}
                  disabled={disabled}
                  onChange={event => updateRow(row.id, { addNewOption: event.target.checked })}
                />
              );
            },
          },
          {
            title: 'Replace',
            dataIndex: 'replace',
            width: 100,
            render: (_, row) => {
              const selectedField = row.customFieldCode
                ? customFieldsByCode.get(row.customFieldCode)
                : undefined;
              const isForcedReplace = isSingleValue(selectedField) && !isSingleValueText(selectedField);
              const disabled = !selectedField || isForcedReplace;

              return (
                <Checkbox
                  checked={selectedField ? isForcedReplace || row.replace : false}
                  disabled={disabled}
                  onChange={event => {
                    if (isMultiValue(selectedField) || isSingleValueText(selectedField)) {
                      updateRow(row.id, { replace: event.target.checked });
                    }
                  }}
                />
              );
            },
          },
          {
            title: 'Max value',
            dataIndex: 'maxValue',
            width: 110,
            render: (_, row) => {
              const selectedField = row.customFieldCode
                ? customFieldsByCode.get(row.customFieldCode)
                : undefined;
              const disabled = !isSingleValueNumber(selectedField);

              return (
                <Checkbox
                  checked={row.maxValue && !disabled}
                  disabled={disabled}
                  onChange={event => updateRow(row.id, { maxValue: event.target.checked })}
                />
              );
            },
          },
          {
            title: '',
            key: 'action',
            width: 70,
            render: (_, row) => (
              <Button
                type="text"
                icon={<DeleteOutlined />}
                onClick={() => removeRow(row.id)}
              />
            ),
          },
        ]}
      />
      <Button icon={<PlusOutlined />} onClick={addRow}>
        Add custom field column
      </Button>
    </Space>
  );
};

const CUSTOM_FIELDS_QUERY = gql`
  query PersonCustomFieldsForImport {
    customFields(criteria: { entityType: PERSON, isActive: true }) {
      hash
      nodes {
        code
        heading
        fieldTypeEnum
        allowMultiple
        allowUserCreate
      }
    }
  }
`;

export default CustomFieldsImportMapping;
